import type { ReactNode } from 'react';

/**
 * Column descriptor for <DataTable />.
 *
 * - `key` is a stable identifier; if no `render` is provided we read
 *   `row[key]` as-is (pass a string key for that to work).
 * - `render(row)` gives full control over the cell content.
 * - `headerClassName` / `cellClassName` are optional per-column Tailwind hooks.
 */
export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  render?: (row: T) => ReactNode;
  headerClassName?: string;
  cellClassName?: string;
}

export interface DataTableProps<T> {
  columns: ReadonlyArray<DataTableColumn<T>>;
  rows: ReadonlyArray<T>;
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  empty?: ReactNode;
  /** Optional test id for the outer wrapper. */
  testId?: string;
}

/**
 * Generic, purely presentational Tailwind table. Pagination is intentionally
 * NOT coupled — callers compose a `<Pagination />` alongside and own the page
 * state. Sorting is also out of scope for the MVP list screens.
 *
 * Accessibility: proper <thead>/<tbody>, and when `onRowClick` is provided
 * each row becomes keyboard-activatable (tabIndex=0 + Enter/Space handler).
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  loading,
  empty,
  testId,
}: DataTableProps<T>) {
  const clickable = Boolean(onRowClick);
  const colCount = columns.length;

  return (
    <div
      className="bg-white rounded border border-slate-200 overflow-hidden"
      data-testid={testId ?? 'data-table'}
    >
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-slate-900">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={
                    'text-left font-medium px-4 py-2 border-b border-slate-200 ' +
                    (col.headerClassName ?? '')
                  }
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr data-testid="data-table-loading">
                <td
                  colSpan={colCount}
                  className="px-4 py-8 text-center text-slate-500"
                >
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr data-testid="data-table-empty">
                <td
                  colSpan={colCount}
                  className="px-4 py-8 text-center text-slate-500"
                >
                  {empty ?? 'No results.'}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const key = rowKey(row);
                const onClick = clickable
                  ? () => onRowClick?.(row)
                  : undefined;
                const onKeyDown = clickable
                  ? (e: React.KeyboardEvent<HTMLTableRowElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick?.(row);
                      }
                    }
                  : undefined;
                return (
                  <tr
                    key={key}
                    data-testid={`data-table-row-${key}`}
                    tabIndex={clickable ? 0 : undefined}
                    role={clickable ? 'button' : undefined}
                    onClick={onClick}
                    onKeyDown={onKeyDown}
                    className={
                      'border-b border-slate-100 last:border-b-0 ' +
                      (clickable
                        ? 'cursor-pointer hover:bg-slate-50 focus:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400'
                        : '')
                    }
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={
                          'px-4 py-2 align-top ' + (col.cellClassName ?? '')
                        }
                      >
                        {col.render
                          ? col.render(row)
                          : renderDefaultCell(row, col.key)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function renderDefaultCell<T>(row: T, key: string): ReactNode {
  const val = (row as unknown as Record<string, unknown>)[key];
  if (val == null) return '';
  if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
    return String(val);
  }
  return '';
}
